import React, { useMemo } from 'react';
import { useRoutes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { loginSelector } from '../redux/auth';
import MainLayout from '../layouts/MainLayout';
import BlankLayout from '../layouts/BlankLayout';
import HistoryTable from '../layouts/components/history-table';
import Editor from '../layouts/components/editor';
import Login from '../pages/login';
import Admin from '../pages/admin';
import CreateNewUser from '../pages/admin/components/createForm';
import EditUserForm from '../pages/admin/components/createForm/EditUserForm';
import NotFound from '../pages/404';
import Home from '../layouts/home/Index';

const PrivateOutlet = React.memo(({ isAuth }) => {
  return isAuth ? <MainLayout /> : <Navigate to="/login" />;
});

const PublicOutlet = React.memo(({ isAuth }) => {
  return isAuth ? <Navigate to="/" /> : <BlankLayout />;
});

const Router = () => {
  const { data } = useSelector(loginSelector);

  const isAdmin = data.user_role === 'admin';
  const isUser = data.user_role === 'user';
  const isAuth = !!data.access_token;

  // Create proper arrays instead of using boolean expressions
  const userRoutes = useMemo(() => {
    return isUser ? [
      { path: '', element: <Home /> },
      { path: 'editor', element: <Editor /> },
      { path: 'history', element: <HistoryTable /> }
    ] : [];
  }, [isUser]);

  const adminRoutes = useMemo(() => {
    return isAdmin ? [
      { path: '', element: <Admin /> },
      { path: 'create', element: <CreateNewUser /> },
      { path: 'edit/:id', element: <EditUserForm /> }
    ] : [];
  }, [isAdmin]);

  // Memoize the entire routes configuration
  const routeConfig = useMemo(() => [
    {
      path: '/',
      element: <PrivateOutlet isAuth={isAuth} />,
      children: userRoutes
    },
    {
      path: '/admin',
      element: <PrivateOutlet isAuth={isAuth} />,
      children: adminRoutes
    },
    {
      path: '/login',
      element: <PublicOutlet isAuth={isAuth} />,
      children: [{ path: '', element: <Login /> }]
    },
    {
      path: '*',
      element: <NotFound />,
    }
  ], [isAuth, userRoutes, adminRoutes]);

  return useRoutes(routeConfig);
};

export default React.memo(Router);
